/**
 * Local Storage Manager for CV Automation
 * 
 * This utility handles storage and retrieval of resume data, summaries, and lists
 * to prevent redundant API calls when switching between tabs.
 * 
 * NOTE: This file was refactored to consolidate duplicate functionality.
 * For job description IDs, use saveJobDescriptionFileId/getJobDescriptionFileId functions.
 * The saveJobDescriptionId/getJobDescriptionId functions are kept for backward compatibility
 * but are deprecated and will be removed in a future version.
 */

// Keys for localStorage
export const STORAGE_KEYS = {
  PDF_URL: 'ca_web_pdf_url',
  PDF_URL_EXPIRY: 'ca_web_pdf_url_expiry',
  SUMMARY: 'ca_web_summary',
  FILE_ID: 'ca_web_file_id',
  COLLECTION_ID: 'ca_web_collection_id',
  ALL_SUMMARIES: 'ca_web_all_summaries',
  ALL_SUMMARIES_TIMESTAMP: 'ca_web_all_summaries_timestamp',
  RESUME_CACHE: 'ca_web_resume_cache', // For storing resume data by ID
  JD_PDF_URL: 'ca_web_jd_pdf_url',
  JD_PDF_URL_EXPIRY: 'ca_web_jd_pdf_url_expiry',
  JD_FILE_ID: 'ca_web_jd_file_id', // The single key for job description ID
  PARSED_JD: 'ca_web_parsed_jd',
  MATCH_RESULT: 'ca_web_match_result',
  JD_TEXT: 'ca_web_jd_text', // For storing text-based job description
};

// Types
export interface CvUrlData {
  url: string;
  timestamp: number;
  expiresInMs: number;
}

export interface Summary {
  cv_id: string;
  fileName: string;
  createdAt: string;
  summary: string;
  collection_id?: string;
  mime?: string;
  user_id?: string;
  upload_time?: string;
  last_update_time?: string;
}

export interface MatchResult {
  score: number;
  details: Record<string, number | string>;
}

// TTL constants (in milliseconds)
const SUMMARIES_LIST_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Store a PDF URL and its expiry information
 */
export const storePdfUrl = (cvId: string, urlData: CvUrlData): void => {
  // Store the URL
  localStorage.setItem(STORAGE_KEYS.PDF_URL, urlData.url);
  
  // Calculate and store expiry timestamp
  const expiryTime = urlData.timestamp + urlData.expiresInMs;
  localStorage.setItem(STORAGE_KEYS.PDF_URL_EXPIRY, expiryTime.toString());
  
  // Also cache this URL with the CV ID for future reference
  const resumeCache = getResumeCache();
  resumeCache[cvId] = {
    url: urlData.url,
    expiry: expiryTime
  };
  localStorage.setItem(STORAGE_KEYS.RESUME_CACHE, JSON.stringify(resumeCache));
};

/**
 * Get the stored PDF URL, but only if it's still valid
 */
export const getPdfUrl = (): string | null => {
  // Check if stored URL is valid
  const expiryTimestamp = localStorage.getItem(STORAGE_KEYS.PDF_URL_EXPIRY);
  if (!expiryTimestamp || parseInt(expiryTimestamp) <= Date.now()) {
    return null; // URL has expired
  }
  
  return localStorage.getItem(STORAGE_KEYS.PDF_URL);
};

/**
 * Get cached PDF URL for a specific CV ID
 */
export const getCachedPdfUrl = (cvId: string): string | null => {
  const resumeCache = getResumeCache();
  const cached = resumeCache[cvId];
  
  if (cached && cached.expiry && cached.url && cached.expiry > Date.now()) {
    return cached.url;
  }
  
  return null;
};

/**
 * Store a summary for a CV
 */
export const storeSummary = (cvId: string, summaryText: string): void => {
  // Store as current summary
  localStorage.setItem(STORAGE_KEYS.SUMMARY, summaryText);
  
  // Also cache this summary with the CV ID for future reference
  const resumeCache = getResumeCache();
  if (!resumeCache[cvId]) {
    resumeCache[cvId] = {};
  }
  resumeCache[cvId].summary = summaryText;
  localStorage.setItem(STORAGE_KEYS.RESUME_CACHE, JSON.stringify(resumeCache));
};

/**
 * Get the stored summary
 */
export const getSummary = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.SUMMARY);
};

/**
 * Get cached summary for a specific CV ID
 */
export const getCachedSummary = (cvId: string): string | null => {
  const resumeCache = getResumeCache();
  const cached = resumeCache[cvId];
  
  return cached?.summary || null;
};

/**
 * Store the file ID
 */
export const storeFileId = (fileId: string): void => {
  localStorage.setItem(STORAGE_KEYS.FILE_ID, fileId);
};

/**
 * Get the stored file ID
 */
export const getFileId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.FILE_ID);
};

/**
 * Store the collection ID
 */
export const storeCollectionId = (collectionId: string): void => {
  localStorage.setItem(STORAGE_KEYS.COLLECTION_ID, collectionId);
};

/**
 * Get the stored collection ID
 */
export const getCollectionId = (): string => {
  return localStorage.getItem(STORAGE_KEYS.COLLECTION_ID) || "scoville";
};

/**
 * Store the list of all summaries
 */
export const storeAllSummaries = (summaries: Summary[]): void => {
  localStorage.setItem(STORAGE_KEYS.ALL_SUMMARIES, JSON.stringify(summaries));
  localStorage.setItem(STORAGE_KEYS.ALL_SUMMARIES_TIMESTAMP, Date.now().toString());
};

/**
 * Get the stored list of summaries, if not expired
 */
export const getAllSummaries = (): Summary[] | null => {
  const timestamp = localStorage.getItem(STORAGE_KEYS.ALL_SUMMARIES_TIMESTAMP);
  
  // Check if we have a valid timestamp and it's not expired
  if (timestamp && (Date.now() - parseInt(timestamp)) < SUMMARIES_LIST_TTL) {
    const summariesJson = localStorage.getItem(STORAGE_KEYS.ALL_SUMMARIES);
    return summariesJson ? JSON.parse(summariesJson) : null;
  }
  
  return null;
};

/**
 * Check if the stored PDF URL is expired
 */
export const isPdfUrlExpired = (): boolean => {
  const expiryTimestamp = localStorage.getItem(STORAGE_KEYS.PDF_URL_EXPIRY);
  if (!expiryTimestamp) return true;
  
  return parseInt(expiryTimestamp) <= Date.now();
};

/**
 * Clear all stored data related to the current resume
 */
export const clearCurrentResumeData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.PDF_URL);
  localStorage.removeItem(STORAGE_KEYS.PDF_URL_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.SUMMARY);
};

// Type for the resume cache entries
interface ResumeCacheEntry {
  url?: string;
  expiry?: number;
  summary?: string;
}

/**
 * Get the resume cache object
 */
export const getResumeCache = (): Record<string, ResumeCacheEntry> => {
  const cache = localStorage.getItem(STORAGE_KEYS.RESUME_CACHE);
  return cache ? JSON.parse(cache) : {};
};

/**
 * Update a summary in the all summaries list without fetching from API
 */
export const updateSummaryInList = (updatedSummary: Summary): void => {
  const summaries = getAllSummaries();
  
  if (!summaries) return;
  
  // Find and update the summary
  const updatedSummaries = summaries.map(summary => 
    summary.cv_id === updatedSummary.cv_id ? updatedSummary : summary
  );
  
  // Store the updated list
  storeAllSummaries(updatedSummaries);
};

/**
 * Save job description file ID
 * This is the preferred function for storing job description IDs
 */
export const saveJobDescriptionFileId = (fileId: string): void => {
  localStorage.setItem(STORAGE_KEYS.JD_FILE_ID, fileId);
};

/**
 * Get job description file ID
 * This is the preferred function for retrieving job description IDs
 */
export const getJobDescriptionFileId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.JD_FILE_ID);
};

/**
 * Save job description PDF URL
 */
export const saveJobDescriptionPdfUrl = (url: string): void => {
  localStorage.setItem(STORAGE_KEYS.JD_PDF_URL, url);
  const expiryTime = Date.now() + (15 * 60 * 1000); // 15 minutes
  localStorage.setItem(STORAGE_KEYS.JD_PDF_URL_EXPIRY, expiryTime.toString());
};

/**
 * Get job description PDF URL if not expired
 */
export const getJobDescriptionPdfUrl = (): string | null => {
  // Check if stored URL is valid
  const expiryTimestamp = localStorage.getItem(STORAGE_KEYS.JD_PDF_URL_EXPIRY);
  if (!expiryTimestamp || parseInt(expiryTimestamp) <= Date.now()) {
    return null; // URL has expired
  }
  
  return localStorage.getItem(STORAGE_KEYS.JD_PDF_URL);
};

/**
 * Save parsed job description
 */
export const saveParsedJobDescription = (parsedContent: string): void => {
  localStorage.setItem(STORAGE_KEYS.PARSED_JD, parsedContent);
};

/**
 * Get parsed job description
 */
export const getParsedJobDescription = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.PARSED_JD);
};

/**
 * Save match result
 */
export const saveMatchResult = (result: MatchResult): void => {
  localStorage.setItem(STORAGE_KEYS.MATCH_RESULT, JSON.stringify(result));
};

/**
 * Get match result
 */
export const getMatchResult = (): MatchResult | null => {
  const result = localStorage.getItem(STORAGE_KEYS.MATCH_RESULT);
  return result ? JSON.parse(result) : null;
};

/**
 * Save job description text
 */
export const saveJobDescriptionText = (text: string): void => {
  localStorage.setItem(STORAGE_KEYS.JD_TEXT, text);
};

/**
 * Get job description text
 */
export const getJobDescriptionText = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.JD_TEXT);
};

// Job Description ID management
/**
 * @deprecated Use saveJobDescriptionFileId instead - both functions serve the same purpose
 */
export const saveJobDescriptionId = (id: string): void => {
  localStorage.setItem(STORAGE_KEYS.JD_FILE_ID, id);
};

/**
 * @deprecated Use getJobDescriptionFileId instead - both functions serve the same purpose
 */
export const getJobDescriptionId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.JD_FILE_ID);
};

// Job Description Collection ID management
export const saveJobDescriptionCollectionId = (id: string): void => {
  localStorage.setItem(STORAGE_KEYS.COLLECTION_ID, id);
};

export const getJobDescriptionCollectionId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.COLLECTION_ID) || 'scoville';
};
